<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Modal from 'dashboard/components/Modal.vue';
import ModalHeader from 'dashboard/components/ModalHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { eligibleTransferTargets } from 'dashboard/helper/inbox';

const props = defineProps({
  show: { type: Boolean, default: false },
  currentChat: { type: Object, required: true },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const allInboxes = useMapGetter('inboxes/getInboxes');
const uiFlags = useMapGetter('inboxConversationTransfers/getUIFlags');

const targetInboxId = ref(null);
const postTransferAction = ref('');
const blockingErrors = ref([]);

watch([targetInboxId, postTransferAction], () => {
  blockingErrors.value = [];
});

const postTransferActions = computed(() => [
  {
    value: 'resolve',
    label: t('CONTACT_PANEL.MOVE_CONVERSATION.MODAL.POST_TRANSFER.RESOLVE'),
    help: t('CONTACT_PANEL.MOVE_CONVERSATION.MODAL.POST_TRANSFER.RESOLVE_HELP'),
    warning: t(
      'CONTACT_PANEL.MOVE_CONVERSATION.MODAL.POST_TRANSFER.RESOLVE_WARNING'
    ),
  },
  {
    value: 'make_primary',
    label: t(
      'CONTACT_PANEL.MOVE_CONVERSATION.MODAL.POST_TRANSFER.MAKE_PRIMARY'
    ),
    help: t(
      'CONTACT_PANEL.MOVE_CONVERSATION.MODAL.POST_TRANSFER.MAKE_PRIMARY_HELP'
    ),
    warning: t(
      'CONTACT_PANEL.MOVE_CONVERSATION.MODAL.POST_TRANSFER.MAKE_PRIMARY_WARNING'
    ),
  },
]);

const selectedActionWarning = computed(() => {
  const selected = postTransferActions.value.find(
    action => action.value === postTransferAction.value
  );
  return selected ? selected.warning : '';
});

const sourceInbox = computed(() =>
  (allInboxes.value || []).find(
    inbox => inbox.id === props.currentChat?.inbox_id
  )
);

const eligibleInboxes = computed(() =>
  sourceInbox.value
    ? eligibleTransferTargets(sourceInbox.value, allInboxes.value || [])
    : []
);

const hasTargets = computed(() => eligibleInboxes.value.length > 0);

const canConfirm = computed(
  () =>
    Boolean(targetInboxId.value) &&
    Boolean(postTransferAction.value) &&
    !uiFlags.value.isCreating
);

const onTargetChange = event => {
  const value = Number(event.target.value);
  targetInboxId.value = Number.isFinite(value) && value > 0 ? value : null;
};

const closeModal = () => {
  targetInboxId.value = null;
  postTransferAction.value = '';
  blockingErrors.value = [];
  emit('close');
};

const confirmMove = async () => {
  if (!canConfirm.value || !sourceInbox.value || !props.currentChat?.id) return;
  blockingErrors.value = [];
  try {
    await store.dispatch('inboxConversationTransfers/createTransfer', {
      inboxId: sourceInbox.value.id,
      payload: {
        target_inbox_id: targetInboxId.value,
        scope: {
          mode: 'selected',
          conversation_display_ids: [props.currentChat.id],
        },
        options: {
          reassign_mode: 'preserve_if_valid',
          auto_assign_open: true,
          preserve_team_if_valid: true,
          preserve_sla: true,
          post_transfer_action: postTransferAction.value,
        },
      },
    });
    useAlert(t('CONTACT_PANEL.MOVE_CONVERSATION.MODAL.SUCCESS'));
    closeModal();
  } catch (error) {
    const errors = error.response?.data?.blocking_errors;
    blockingErrors.value =
      Array.isArray(errors) && errors.length
        ? errors
        : [
            error.response?.data?.error ||
              t('CONTACT_PANEL.MOVE_CONVERSATION.MODAL.ERROR'),
          ];
  }
};
</script>

<template>
  <Modal :show="show" :on-close="closeModal" @close="closeModal">
    <div class="flex flex-col">
      <ModalHeader
        :header-title="$t('CONTACT_PANEL.MOVE_CONVERSATION.MODAL.TITLE')"
        :header-content="
          $t('CONTACT_PANEL.MOVE_CONVERSATION.MODAL.DESCRIPTION')
        "
      />
      <div class="flex flex-col gap-4 px-8 pt-6 pb-8">
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">
            {{ $t('CONTACT_PANEL.MOVE_CONVERSATION.MODAL.SELECT_LABEL') }}
          </label>
          <select
            :value="targetInboxId || ''"
            :disabled="!hasTargets"
            @change="onTargetChange"
          >
            <option value="" disabled>
              {{
                $t('CONTACT_PANEL.MOVE_CONVERSATION.MODAL.SELECT_PLACEHOLDER')
              }}
            </option>
            <option
              v-for="inbox in eligibleInboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
          <p v-if="!hasTargets" class="mt-2 text-sm text-n-slate-11">
            {{ $t('CONTACT_PANEL.MOVE_CONVERSATION.MODAL.NO_TARGETS') }}
          </p>
        </div>

        <div v-if="hasTargets">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">
            {{
              $t('CONTACT_PANEL.MOVE_CONVERSATION.MODAL.POST_TRANSFER_LABEL')
            }}
          </label>
          <div class="flex flex-col gap-2">
            <label
              v-for="action in postTransferActions"
              :key="action.value"
              class="flex gap-2 items-start text-sm text-n-slate-12 cursor-pointer"
            >
              <input
                v-model="postTransferAction"
                type="radio"
                :value="action.value"
                class="mt-1 m-0"
              />
              <span class="flex gap-1 items-center">
                {{ action.label }}
                <Icon
                  v-tooltip.top="action.help"
                  icon="i-lucide-info"
                  class="flex-shrink-0 w-4 h-4 cursor-help text-n-slate-9"
                />
              </span>
            </label>
          </div>
        </div>

        <div
          v-if="selectedActionWarning"
          class="p-3 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-900/20"
        >
          <p class="text-sm text-amber-800 dark:text-amber-100">
            {{ selectedActionWarning }}
          </p>
        </div>

        <div
          v-if="blockingErrors.length"
          class="p-3 rounded-md border border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-900/20"
        >
          <ul class="pl-4 list-disc text-sm text-red-700 dark:text-red-200">
            <li v-for="(message, index) in blockingErrors" :key="index">
              {{ message }}
            </li>
          </ul>
        </div>

        <div class="flex gap-2 justify-end">
          <NextButton
            faded
            slate
            :label="$t('CONTACT_PANEL.MOVE_CONVERSATION.MODAL.CANCEL')"
            @click="closeModal"
          />
          <NextButton
            :label="$t('CONTACT_PANEL.MOVE_CONVERSATION.MODAL.CONFIRM')"
            :disabled="!canConfirm"
            :is-loading="uiFlags.isCreating"
            @click="confirmMove"
          />
        </div>
      </div>
    </div>
  </Modal>
</template>
